<script setup>
import { computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { isAttributeVisible } from 'dashboard/helper/customAttributeConditions';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const currentChat = computed(() => getters.getSelectedChat.value);
const customAttributes = computed(
  () => currentChat.value.custom_attributes || {}
);

const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value('conversation_attribute')
);

const pinnedVisibleAttributes = computed(() =>
  attributes.value
    .filter(attribute => attribute.pinned)
    .filter(attribute => isAttributeVisible(attribute, customAttributes.value))
    .map(attribute => ({
      ...attribute,
      value:
        attribute.attribute_key in customAttributes.value
          ? customAttributes.value[attribute.attribute_key]
          : '',
    }))
);

const onUpdate = async (key, value) => {
  const updatedAttributes = { ...customAttributes.value, [key]: value };
  try {
    await store.dispatch('updateCustomAttributes', {
      conversationId: props.conversationId,
      customAttributes: updatedAttributes,
    });
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const onDelete = async key => {
  try {
    const { [key]: remove, ...updatedAttributes } = customAttributes.value;
    await store.dispatch('updateCustomAttributes', {
      conversationId: props.conversationId,
      customAttributes: updatedAttributes,
    });
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

const onCopy = async attributeValue => {
  await copyTextToClipboard(attributeValue);
  useAlert(t('CUSTOM_ATTRIBUTES.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div v-if="pinnedVisibleAttributes.length" class="flex flex-col gap-2">
    <div
      v-for="attribute in pinnedVisibleAttributes"
      :key="attribute.attribute_key"
      class="rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
    >
      <CustomAttribute
        :attribute-key="attribute.attribute_key"
        :attribute-type="attribute.attribute_display_type"
        :values="attribute.attribute_values"
        :label="attribute.attribute_display_name"
        :description="attribute.attribute_description"
        :value="attribute.value"
        show-actions
        :attribute-regex="attribute.regex_pattern"
        :regex-cue="attribute.regex_cue"
        @update="onUpdate"
        @delete="onDelete"
        @copy="onCopy"
      />
    </div>
  </div>
</template>
